import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Banknote, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { money } from "../lib/format";

const STATUS_COLOR = {
  COMPLETED: "#2F6B4F", AWAITING_APPROVAL: "#B45309", ACCEPTED: "#0E3A5C",
  PROCESSING: "#0E3A5C", SUBMITTED: "#0E3A5C", REJECTED: "#B45309", RETURNED: "#B45309",
};
const STATUS_LABEL = { AWAITING_APPROVAL: "Awaiting bank approval" };
const label = (status) => STATUS_LABEL[status] || status;

const today = () => new Date().toISOString().slice(0, 10);

/** Approved invoices → J.P. Morgan Access template payments. Bank delivery is MOCKED for now. */
export default function AdminAch() {
  const [eligible, setEligible] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [executionDate, setExecutionDate] = useState(today());
  const [mode, setMode] = useState("mock");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: e }, { data: b }] = await Promise.all([
      api.get("/admin/ach/eligible"), api.get("/admin/ach/batches"),
    ]);
    setEligible(e.invoices);
    setMode(e.mode);
    setBatches(b.batches);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const total = eligible.filter((i) => selected.includes(i.id)).reduce((a, b) => a + b.amount, 0);

  const submit = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Submit ${selected.length} payment(s) totalling ${money(total)} against their Access templates? A second administrator must approve them in Access.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ach/batches", {
        invoice_ids: selected, execution_date: executionDate,
      });
      toast.success(`${data.count} payment(s) submitted as ${data.reference} — awaiting approval in Access`);
      setSelected([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const refresh = async (batch) => {
    try {
      await api.post(`/admin/ach/batches/${batch.id}/refresh`);
      toast.success("Bank status refreshed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-ach">
      <div className="flex items-start gap-2 border p-4 mb-6" style={{ borderColor: "#B45309", background: "rgba(180,83,9,0.06)" }} data-testid="ach-mode-note">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: "#B45309" }}/>
        <p className="text-sm" style={{ color: "var(--ink)" }}>
          Payments are submitted against your pre-defined <span className="font-bold">J.P. Morgan Access templates</span>,
          and a second administrator approves each transaction inside Access.
          <span className="font-bold"> Bank delivery is MOCKED</span> ({mode} adapter) — nothing reaches
          J.P. Morgan and no money moves. Once your banker enables API access, set
          {" "}<span className="font-mono text-xs">BANK_MODE=jpm</span> with the JPM credentials and this
          same screen submits the real template payments.
        </p>
      </div>

      <div className="border p-6 mb-8" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <h3 className="font-heading font-bold text-lg mb-1" style={{ color: "var(--ink)" }}>Approved invoices ready to pay</h3>
        <p className="text-xs mb-5" style={{ color: "var(--muted)" }}>
          Select the invoices to include, set the effective date, then submit. Each payment goes out
          against the employer's Access template for that vendor — assign any missing templates on
          the Vendors page first.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="ach-eligible-table">
            <thead>
              <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
                <th className="px-3 py-3 w-10"></th>
                <th className="px-3 py-3">Employer</th>
                <th className="px-3 py-3">Vendor</th>
                <th className="px-3 py-3">Invoice #</th>
                <th className="px-3 py-3">Funds</th>
                <th className="px-3 py-3">Access template</th>
                <th className="px-3 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {eligible.map((inv) => (
                <tr key={inv.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`ach-eligible-${inv.id}`}>
                  <td className="px-3 py-2.5">
                    <input type="checkbox" checked={selected.includes(inv.id)} disabled={!inv.payment_template}
                      onChange={() => toggle(inv.id)} data-testid={`ach-select-${inv.id}`}/>
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "var(--ink)" }}>{inv.company || "—"}</td>
                  <td className="px-3 py-2.5" style={{ color: "var(--ink)" }}>{inv.vendor_name}</td>
                  <td className="px-3 py-2.5 font-mono text-xs" style={{ color: "var(--muted)" }}>{inv.invoice_number}</td>
                  <td className="px-3 py-2.5"><span className="pill text-[10px]">{inv.fund_source}</span></td>
                  <td className="px-3 py-2.5 text-xs font-mono" style={{ color: inv.payment_template ? "var(--muted)" : "#B45309" }} data-testid={`ach-template-${inv.id}`}>
                    {inv.payment_template || "No template assigned"}
                  </td>
                  <td className="px-3 py-2.5 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>{money(inv.amount)}</td>
                </tr>
              ))}
              {!eligible.length && (
                <tr><td colSpan={7} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="ach-eligible-empty">
                  No approved invoices are waiting for payment.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-5 flex flex-wrap items-end gap-4">
          <div>
            <label className="field-label">Effective date</label>
            <input className="input-flat" type="date" value={executionDate} onChange={(e) => setExecutionDate(e.target.value)} data-testid="ach-execution-date"/>
          </div>
          <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="ach-selection-total">
            {selected.length} selected · {money(total)}
          </p>
          <button onClick={submit} disabled={busy || !selected.length} className="btn-primary flex items-center gap-2" data-testid="ach-submit-batch">
            <Send size={14}/> {busy ? "Submitting…" : "Submit payments"}
          </button>
        </div>
      </div>

      <h3 className="font-heading font-bold text-lg mb-3" style={{ color: "var(--ink)" }}>Submitted payments</h3>
      <div className="space-y-4" data-testid="ach-batches">
        {batches.map((batch) => (
          <div key={batch.id} className="border p-5" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid={`ach-batch-${batch.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-heading font-bold flex items-center gap-2" style={{ color: "var(--ink)" }}>
                  <Banknote size={14} style={{ color: "var(--muted)" }}/> {batch.reference}
                  <span className="pill text-[10px]" style={{ color: STATUS_COLOR[batch.status] }}>{label(batch.status)}</span>
                </p>
                <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                  {batch.count} payment(s) · {money(batch.total)} · effective {batch.execution_date} ·
                  {" "}{batch.created_by} · {batch.mode} adapter
                </p>
              </div>
              <button onClick={() => refresh(batch)} className="btn-ghost flex items-center gap-2" data-testid={`ach-refresh-${batch.id}`}>
                <RefreshCw size={14}/> Refresh status
              </button>
            </div>
            <ul className="mt-4 divide-y" style={{ borderColor: "var(--border)" }}>
              {batch.payments.map((payment) => (
                <li key={payment.invoice_id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`ach-payment-${payment.invoice_id}`}>
                  <span style={{ color: "var(--ink)" }}>
                    {payment.vendor_name} · <span className="font-mono text-xs">{payment.invoice_number}</span>
                    <span className="ml-2 text-xs" style={{ color: "var(--muted)" }}>{payment.company}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-xs font-mono" style={{ color: "var(--muted)" }}>{payment.payment_template}</span>
                    <span className="pill text-[10px]" style={{ color: STATUS_COLOR[payment.status] }}>{label(payment.status)}</span>
                    <span className="font-heading font-bold" style={{ color: "var(--ink)" }}>{money(payment.amount)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {!batches.length && (
          <p className="border p-10 text-center text-sm" style={{ borderColor: "var(--border)", background: "var(--surface)", color: "var(--muted)" }} data-testid="ach-batches-empty">
            No payments submitted yet.
          </p>
        )}
      </div>
    </div>
  );
}
